import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Shield, Eye, Share2, Lock, UserCheck, Cookie, Database, Trash2, Baby, Globe, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PoliticaPrivacidade = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" /> Voltar ao início
        </Link>

        <div className="mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-4">
            Privacidade & LGPD
          </span>
          <h1 className="text-4xl font-bold mb-2">Política de Privacidade</h1>
          <p className="text-muted-foreground">Última atualização: 09 de março de 2026</p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <Clock className="h-4 w-4" />
            <span>Tempo estimado de leitura: 20 minutos</span>
          </div>
        </div>

        {/* Intro */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <p className="text-muted-foreground leading-relaxed">
            O <strong className="text-foreground">ViaMenu</strong> respeita a sua privacidade e está comprometido em proteger os dados pessoais que Você nos confia. Esta Política de Privacidade descreve como coletamos, utilizamos, armazenamos, compartilhamos e protegemos suas informações pessoais, em conformidade com a <strong className="text-foreground">Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/18)</strong> e demais legislações aplicáveis.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            Ao utilizar a Plataforma ViaMenu, Você declara estar ciente e de acordo com os termos desta Política. Caso não concorde, recomendamos que não utilize nossos serviços.
          </p>
        </div>

        {/* Quick overview */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Shield className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-xs font-medium">Dados protegidos</p>
            <p className="text-xs text-muted-foreground mt-1">Criptografia TLS em trânsito e em repouso.</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Eye className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-xs font-medium">Transparência total</p>
            <p className="text-xs text-muted-foreground mt-1">Você sabe exatamente quais dados coletamos e por quê.</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <UserCheck className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-xs font-medium">Seus direitos garantidos</p>
            <p className="text-xs text-muted-foreground mt-1">Acesse, corrija, exclua ou exporte seus dados a qualquer momento.</p>
          </div>
        </div>

        {/* Sections */}
        <Accordion type="multiple" defaultValue={["1"]} className="space-y-3">
          <AccordionItem value="1" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Database className="h-5 w-5 text-primary" /> 1. Dados que Coletamos</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <h4 className="font-semibold text-foreground">1.1. Dados fornecidos por Você</h4>
              <p>Ao se cadastrar e utilizar a Plataforma, coletamos:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Dados de identificação:</strong> Nome completo, e-mail, telefone, CPF/CNPJ;</li>
                <li><strong className="text-foreground">Dados do estabelecimento:</strong> Nome do restaurante, endereço, tipo de cozinha, horário de funcionamento, logotipo;</li>
                <li><strong className="text-foreground">Dados financeiros:</strong> Informações necessárias para processamento de pagamentos (gerenciadas pelo Mercado Pago — não armazenamos dados completos de cartão);</li>
                <li><strong className="text-foreground">Conteúdo do cardápio:</strong> Produtos, categorias, preços, descrições, imagens;</li>
                <li><strong className="text-foreground">Dados de pedidos:</strong> Histórico de pedidos, endereços de entrega, preferências de pagamento;</li>
                <li><strong className="text-foreground">Comunicações:</strong> Mensagens enviadas ao suporte, avaliações, comentários.</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">1.2. Dados coletados automaticamente</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Dados de navegação:</strong> Endereço IP, tipo de navegador, sistema operacional, páginas acessadas, tempo de sessão;</li>
                <li><strong className="text-foreground">Dados de dispositivo:</strong> Modelo do dispositivo, identificadores únicos, resolução de tela;</li>
                <li><strong className="text-foreground">Dados de localização:</strong> Localização aproximada (baseada no IP) para funcionalidades de delivery;</li>
                <li><strong className="text-foreground">Cookies e tecnologias similares:</strong> Conforme descrito na seção 7 desta Política;</li>
                <li><strong className="text-foreground">Dados de uso:</strong> Funcionalidades utilizadas, cliques, interações, relatórios gerados, integrações ativas.</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">1.3. Dados de terceiros</h4>
              <p>
                Podemos receber dados de parceiros de integração (iFood, Mercado Pago) quando Você ativa essas integrações, incluindo: dados de pedidos sincronizados, status de pagamentos e informações de transações.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="2" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Eye className="h-5 w-5 text-primary" /> 2. Como Utilizamos seus Dados</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Utilizamos seus dados pessoais para as seguintes finalidades, sempre com base legal adequada (LGPD, art. 7º):</p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 font-semibold text-foreground">Finalidade</th>
                      <th className="text-left py-2 font-semibold text-foreground">Base legal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="py-2 pr-4">Criação e gestão da sua conta</td><td className="py-2">Execução de contrato</td></tr>
                    <tr><td className="py-2 pr-4">Prestação dos serviços (cardápio, pedidos, delivery, PDV)</td><td className="py-2">Execução de contrato</td></tr>
                    <tr><td className="py-2 pr-4">Processamento de pagamentos e cobranças</td><td className="py-2">Execução de contrato</td></tr>
                    <tr><td className="py-2 pr-4">Envio de notificações sobre pedidos e status</td><td className="py-2">Execução de contrato</td></tr>
                    <tr><td className="py-2 pr-4">Geração de relatórios e analytics para o restaurante</td><td className="py-2">Execução de contrato</td></tr>
                    <tr><td className="py-2 pr-4">Funcionalidades de IA (descrições, sugestões, marketing)</td><td className="py-2">Consentimento</td></tr>
                    <tr><td className="py-2 pr-4">Envio de comunicações de marketing e novidades</td><td className="py-2">Legítimo interesse / Consentimento</td></tr>
                    <tr><td className="py-2 pr-4">Melhoria e personalização da Plataforma</td><td className="py-2">Legítimo interesse</td></tr>
                    <tr><td className="py-2 pr-4">Prevenção de fraudes e segurança</td><td className="py-2">Legítimo interesse</td></tr>
                    <tr><td className="py-2 pr-4">Cumprimento de obrigações legais e regulatórias</td><td className="py-2">Obrigação legal</td></tr>
                    <tr><td className="py-2 pr-4">Programas de fidelidade e cashback</td><td className="py-2">Execução de contrato</td></tr>
                  </tbody>
                </table>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="3" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Share2 className="h-5 w-5 text-primary" /> 3. Compartilhamento de Dados</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>
                O ViaMenu <strong className="text-foreground">não vende, aluga ou comercializa</strong> seus dados pessoais. Compartilhamos informações apenas nas seguintes situações:
              </p>

              <div className="space-y-4 mt-3">
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">Processadores de pagamento</h4>
                  <p className="text-sm">Mercado Pago — para processamento de cobranças, pagamentos via cartão e PIX. Sujeito à <a href="https://www.mercadopago.com.br/privacidade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">política de privacidade do Mercado Pago</a>.</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">Integrações ativadas pelo Usuário</h4>
                  <p className="text-sm">Quando Você ativa integrações (iFood, etc.), dados de cardápio e pedidos são compartilhados com esses serviços conforme necessário para a funcionalidade.</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">Provedores de infraestrutura</h4>
                  <p className="text-sm">Serviços de hospedagem, banco de dados e CDN que processam dados em nosso nome, sob contratos de proteção de dados (DPA).</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">Provedores de IA</h4>
                  <p className="text-sm">Quando Você utiliza funcionalidades de IA (geração de descrições, sugestões, marketing), os prompts são enviados a provedores de modelos de linguagem. Não incluímos dados pessoais identificáveis nos prompts.</p>
                </div>

                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">Obrigações legais</h4>
                  <p className="text-sm">Quando exigido por lei, ordem judicial, investigação policial ou para proteger direitos, propriedade ou segurança do ViaMenu e seus usuários.</p>
                </div>
              </div>

              <div className="bg-muted rounded-lg p-4 mt-3 border-l-4 border-primary">
                <p className="text-sm">
                  <strong className="text-foreground">Entre restaurante e consumidor:</strong> Quando um consumidor faz um pedido, o restaurante recebe os dados necessários para a entrega (nome, telefone, endereço). O restaurante é responsável por proteger esses dados conforme a LGPD.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="4" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Lock className="h-5 w-5 text-primary" /> 4. Segurança dos Dados</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Implementamos medidas técnicas e organizacionais para proteger seus dados:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Criptografia em trânsito:</strong> Todas as comunicações utilizam TLS 1.2+ (HTTPS);</li>
                <li><strong className="text-foreground">Criptografia em repouso:</strong> Dados armazenados em bancos de dados com criptografia AES-256;</li>
                <li><strong className="text-foreground">Controle de acesso:</strong> Políticas de Row Level Security (RLS) garantem que cada usuário acesse apenas seus próprios dados;</li>
                <li><strong className="text-foreground">Autenticação segura:</strong> Senhas armazenadas com hashing bcrypt, suporte a autenticação multifator;</li>
                <li><strong className="text-foreground">Monitoramento:</strong> Logs de auditoria para ações sensíveis e detecção de atividades suspeitas;</li>
                <li><strong className="text-foreground">Backups:</strong> Backups automáticos diários com retenção de 30 dias;</li>
                <li><strong className="text-foreground">Dados de pagamento:</strong> Processados exclusivamente pelo Mercado Pago (certificação PCI DSS Nível 1) — não armazenamos números de cartão.</li>
              </ul>

              <div className="bg-muted rounded-lg p-4 mt-3">
                <p className="text-sm">
                  Embora adotemos as melhores práticas de segurança, nenhum sistema é 100% inviolável. Em caso de incidente de segurança que envolva dados pessoais, notificaremos os titulares afetados e a ANPD conforme determina a LGPD (art. 48).
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="5" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><UserCheck className="h-5 w-5 text-primary" /> 5. Seus Direitos (LGPD)</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>
                Conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/18), Você, como titular dos dados, tem os seguintes direitos:
              </p>
              <div className="grid gap-3 mt-3">
                {[
                  { right: "Confirmação e acesso", desc: "Confirmar se tratamos seus dados e acessar uma cópia deles." },
                  { right: "Correção", desc: "Solicitar a correção de dados incompletos, inexatos ou desatualizados." },
                  { right: "Anonimização ou bloqueio", desc: "Solicitar anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos." },
                  { right: "Portabilidade", desc: "Solicitar a portabilidade dos seus dados a outro fornecedor de serviço." },
                  { right: "Eliminação", desc: "Solicitar a eliminação dos dados tratados com base no seu consentimento." },
                  { right: "Informação sobre compartilhamento", desc: "Ser informado sobre entidades públicas e privadas com as quais compartilhamos seus dados." },
                  { right: "Revogação do consentimento", desc: "Revogar o consentimento a qualquer momento, sem afetar a licitude do tratamento anterior." },
                  { right: "Oposição", desc: "Opor-se ao tratamento realizado com base em legítimo interesse, caso aplicável." },
                  { right: "Revisão de decisões automatizadas", desc: "Solicitar revisão de decisões tomadas exclusivamente com base em tratamento automatizado." },
                ].map((item) => (
                  <div key={item.right} className="bg-muted rounded-lg p-3">
                    <p className="text-sm"><strong className="text-foreground">{item.right}:</strong> {item.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3">
                Para exercer qualquer um desses direitos, entre em contato com nosso Encarregado de Proteção de Dados (DPO) pelo e-mail <a href="mailto:privacidade@viamenu.com.br" className="text-primary hover:underline">privacidade@viamenu.com.br</a>. Responderemos sua solicitação em até 15 dias úteis.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="6" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Trash2 className="h-5 w-5 text-primary" /> 6. Retenção e Exclusão de Dados</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Mantemos seus dados pelos seguintes períodos:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm mt-3">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 font-semibold text-foreground">Tipo de dado</th>
                      <th className="text-left py-2 font-semibold text-foreground">Período de retenção</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="py-2 pr-4">Dados da conta ativa</td><td className="py-2">Enquanto a conta estiver ativa</td></tr>
                    <tr><td className="py-2 pr-4">Dados após cancelamento</td><td className="py-2">90 dias para exportação, depois excluídos</td></tr>
                    <tr><td className="py-2 pr-4">Dados financeiros e fiscais</td><td className="py-2">5 anos (obrigação legal — CTN art. 173)</td></tr>
                    <tr><td className="py-2 pr-4">Logs de acesso (Marco Civil)</td><td className="py-2">6 meses (Lei nº 12.965/14, art. 15)</td></tr>
                    <tr><td className="py-2 pr-4">Dados de pedidos (consumidor)</td><td className="py-2">5 anos (prazo prescricional CDC)</td></tr>
                    <tr><td className="py-2 pr-4">Dados de marketing (com consentimento)</td><td className="py-2">Até revogação do consentimento</td></tr>
                    <tr><td className="py-2 pr-4">Backups</td><td className="py-2">30 dias após exclusão dos dados primários</td></tr>
                  </tbody>
                </table>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="7" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Cookie className="h-5 w-5 text-primary" /> 7. Cookies e Tecnologias de Rastreamento</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>A Plataforma ViaMenu utiliza as seguintes categorias de cookies:</p>

              <div className="space-y-3 mt-3">
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">🟢 Cookies essenciais (obrigatórios)</h4>
                  <p className="text-sm">Necessários para o funcionamento básico da Plataforma: autenticação, sessão, preferências de idioma e segurança. Não podem ser desativados.</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">🔵 Cookies analíticos</h4>
                  <p className="text-sm">Utilizados para entender como os usuários interagem com a Plataforma: páginas mais visitadas, funcionalidades mais usadas, origem do tráfego. Dados anonimizados.</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-foreground text-sm mb-1">🟡 Cookies de funcionalidade</h4>
                  <p className="text-sm">Permitem funcionalidades avançadas como tema (claro/escuro), preferências de exibição e configurações salvas.</p>
                </div>
              </div>

              <p className="mt-3">
                Você pode gerenciar suas preferências de cookies a qualquer momento nas configurações do navegador. A desativação de cookies não essenciais pode afetar algumas funcionalidades da Plataforma.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="8" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Baby className="h-5 w-5 text-primary" /> 8. Dados de Menores</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>
                A Plataforma ViaMenu não é direcionada a menores de 16 anos. Não coletamos intencionalmente dados pessoais de crianças ou adolescentes menores de 16 anos. Caso tome conhecimento de que um menor forneceu dados pessoais sem o consentimento dos pais ou responsáveis legais, entre em contato conosco para que possamos tomar as medidas necessárias para excluir essas informações.
              </p>
              <p>
                Para usuários entre 16 e 18 anos, o acesso à Plataforma como consumidor é permitido, conforme previsto nos <Link to="/termos" className="text-primary hover:underline">Termos de Uso</Link>.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="9" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Globe className="h-5 w-5 text-primary" /> 9. Transferência Internacional de Dados</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>
                Alguns de nossos provedores de infraestrutura e serviços podem estar localizados fora do Brasil. Quando houver transferência internacional de dados, garantimos que:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>O país de destino proporcione grau de proteção adequado, conforme avaliação da ANPD;</li>
                <li>Sejam adotadas cláusulas contratuais padrão (SCCs) ou outros mecanismos de proteção previstos na LGPD (art. 33);</li>
                <li>Os dados sejam protegidos com os mesmos padrões de segurança descritos nesta Política.</li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="10" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary" /> 10. Contato e Encarregado (DPO)</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Para exercer seus direitos, esclarecer dúvidas ou fazer reclamações sobre o tratamento de dados pessoais:</p>

              <div className="bg-muted rounded-lg p-4 mt-3 space-y-2">
                <p className="text-sm"><strong className="text-foreground">Encarregado de Proteção de Dados (DPO):</strong></p>
                <p className="text-sm">E-mail: <a href="mailto:privacidade@viamenu.com.br" className="text-primary hover:underline">privacidade@viamenu.com.br</a></p>
                <p className="text-sm">Prazo de resposta: até 15 dias úteis</p>
              </div>

              <p className="mt-3 text-sm">
                Caso não fique satisfeito com nossa resposta, Você tem o direito de apresentar reclamação à <strong className="text-foreground">Autoridade Nacional de Proteção de Dados (ANPD)</strong> — <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.gov.br/anpd</a>.
              </p>

              <h4 className="font-semibold text-foreground mt-4">Alterações nesta Política</h4>
              <p>
                Esta Política pode ser atualizada periodicamente. Alterações significativas serão comunicadas por e-mail e/ou banner na Plataforma com antecedência mínima de 30 dias. A versão mais recente estará sempre disponível nesta página.
              </p>

              <p className="mt-4 text-sm">
                Estamos à disposição para cuidar dos seus dados com responsabilidade e transparência. Conte conosco! 🧡
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default PoliticaPrivacidade;
